import { buildWatermarkText, type WatermarkInput } from "@dataroom/shared";

export { buildWatermarkText, type WatermarkInput };

const TILE_X = 280;
const TILE_Y = 120;

/** Burn a diagonal tiled watermark into a 2D canvas (preview and image downloads). */
export function burnWatermarkOnCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  text: string,
): void {
  const fontSize = Math.max(12, Math.min(16, width / 55));
  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = "#000";
  ctx.font = `600 ${fontSize}px Helvetica, Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  for (let y = -height; y < height * 2; y += TILE_Y) {
    for (let x = -width; x < width * 2; x += TILE_X) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((-28 * Math.PI) / 180);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.font = "7px Helvetica, Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(text, 12, 10);
  ctx.textBaseline = "bottom";
  ctx.fillText(text, 12, height - 10);
  ctx.restore();
}

export async function watermarkImageBlob(blob: Blob, text: string): Promise<Blob> {
  const img = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    img.close();
    throw new Error("watermark_canvas_unavailable");
  }
  ctx.drawImage(img, 0, 0);
  burnWatermarkOnCanvas(ctx, canvas.width, canvas.height, text);
  img.close();

  const mime = blob.type && blob.type.startsWith("image/") ? blob.type : "image/png";
  const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, 0.92));
  if (!out) throw new Error("watermark_failed");
  return out;
}

export function watermarkTextFromParts(input: WatermarkInput): string {
  return buildWatermarkText(input);
}

/** Burn only when a viewer identity is present. Timestamp-only text still re-encodes JPEGs. */
export function shouldBurnImageWatermark(input: WatermarkInput): boolean {
  return Boolean(input.email || input.viewId);
}
